/**
 * 通知相關類型定義
 */

/**
 * 通知類型枚舉
 */
export enum NotificationType {
  // 組織管理
  OrganizationInvitation = 'organization_invitation',
  OrganizationMemberJoined = 'organization_member_joined',
  OrganizationMemberRemoved = 'organization_member_removed',
  OrganizationRoleChanged = 'organization_role_changed',

  // 支付與訂閱
  PaymentSuccess = 'payment_success',
  PaymentFailed = 'payment_failed',
  SubscriptionCreated = 'subscription_created',
  SubscriptionRenewed = 'subscription_renewed',
  SubscriptionCancelled = 'subscription_cancelled',
  SubscriptionExpiringSoon = 'subscription_expiring_soon',
  InvoiceGenerated = 'invoice_generated',
  WalletRecharged = 'wallet_recharged',
  WalletLowBalance = 'wallet_low_balance',

  // Feedback 反饋
  FeedbackCommented = 'feedback_commented',
  FeedbackReplied = 'feedback_replied',
  FeedbackStatusChanged = 'feedback_status_changed',
  FeedbackMerged = 'feedback_merged',
  FeedbackPatchReady = 'feedback_patch_ready',
  FeedbackPatchFailed = 'feedback_patch_failed',

  // 用戶安全
  Welcome = 'welcome',
  PasswordResetSuccess = 'password_reset_success',
  TwoFactorEnabled = '2fa_enabled',
  TwoFactorDisabled = '2fa_disabled',
  UnusualLogin = 'unusual_login',

  // 業務處理
  AiAppPublished = 'ai_app_published',
  DatasourceProcessed = 'datasource_processed',
  DatasourceFailed = 'datasource_failed',
  /** slot 月度 MXC 用量達預警閾值（僅通知組織所有者） */
  SlotUsageAlert = 'slot_usage_alert',

  // OAuth 授權
  OauthAppAuthorized = 'oauth_app_authorized',
  OauthAppRevoked = 'oauth_app_revoked',

  // 審批（Business #1259）：有一筆待你審批的 Agent 操作。只是提醒，狀態以審批詳情為準
  ApprovalPending = 'approval_pending',

  // 系統
  System = 'system',
  Announcement = 'announcement',
}

/** 通知關聯的是一筆審批請求；`related_uuid` 即審批詳情的 `request_id` */
export const APPROVAL_REQUEST_RELATED_TYPE = 'agent_authorization_request';

/**
 * 強制送達、使用者不能在通知設定裡關掉的類型（後端會忽略對它們的關閉設定）。
 * 設定頁列可關閉類型時要排除；送出設定時也不能把它們放進 disabled_types。
 */
export const MANDATORY_NOTIFICATION_TYPES: readonly string[] = [
  NotificationType.ApprovalPending,
  NotificationType.System,
  NotificationType.Announcement,
];

export interface Notification {
  uuid: string;
  type: NotificationType | string;
  title: string;
  content: string;
  sender_user_id: number;
  sender_name: string;
  sender_avatar: string | null;
  related_type: string | null;
  related_uuid: string | null;
  related_url: string | null;
  metadata: string | null;
  is_read: boolean;
  read_at: number;
  is_archived: boolean;
  archived_at: number;
  created_at: number;
}

export interface NotificationPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export interface NotificationListResponse {
  notifications: Notification[];
  pagination: NotificationPagination;
}

export interface UnreadCountResponse {
  unread_count: number;
}

export interface MarkReadRequest {
  uuids: string[];
}

export interface MarkReadResponse {
  success_count: number;
  failed_count: number;
  failed_uuids: string[];
}

export interface DeleteNotificationsRequest {
  uuids: string[];
}

export interface DeleteNotificationsResponse {
  success_count: number;
  failed_count: number;
  /** 僅在 failed_count > 0 時由後端回傳 */
  failed_uuids?: string[];
}

export interface ArchiveNotificationsRequest {
  uuids: string[];
}

export interface ArchiveNotificationsResponse {
  success_count: number;
  failed_count: number;
  /** 僅在 failed_count > 0 時由後端回傳 */
  failed_uuids?: string[];
}

export interface NotificationSettings {
  user_id: number;
  workspace_uuid: string | null;
  enabled_types: string[];
  disabled_types: string[];
  updated_at: number;
}

export interface UpdateNotificationSettingsRequest {
  enabled_types: string[];
  disabled_types: string[];
}

export interface GetNotificationsParams {
  type?: string;
  is_read?: boolean;
  is_archived?: boolean;
  page?: number;
  page_size?: number;
}
